/* eslint-disable no-undef */
/* eslint-disable consistent-return */
import store from '@Store';
import { NOTIFICATION_HANDLER, DOCUMENT_SEARCH, DOCUMENT_MARKUP } from '@Constants/types';
import { LOG_TYPE, addLog } from './loggingService';
import { sortByRiskSeverity, sortByStartOffset } from '@Utils/jsonUtils';
import { isWordonline } from '@Utils/commonUtils';
import MarkupWorker from '../workers/markup.worker.js';
import { STORAGE_TYPE, getFromStorage } from '@Service/storageService';

const markupWorker = new MarkupWorker();
/* global Word */

const policyColour = '#5b8bff';
const missesNotFoundColour = 'Black';
const missesFoundColour = 'LightGray';
const highRiskColour = 'Red';
const mediumRiskColour = '#ffc801';
const lowRiskColour = '#33cc33';
const naRiskColour = 'Gray';

let ooxmlString = '';
let searchCount = 0;

/* WILDCARD METHODS START HERE */

function removeLeadingWilstcards(text) {
    try {
        if (text && text.length > 0) {
            let fisrtChar = text.substring(0, 1);
            while (fisrtChar === '?' && text.length > 0) {
                text = text.substring(1); // trim leading '?' character
                fisrtChar = text.substring(0, 1);
            }
        }

        return text;
    } catch (error) {
        addLog(LOG_TYPE.ERROR, 'wordApiService.removeLeadingWilstcards', error);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload:
                'Something went wrong with the Word add-in API, please try again. Contact your system administrator if this issue persists.'
        });
    }
}

function removeTrailingWildcards(text) {
    try {
        if (text && text.length > 0) {
            let lastChar = text.substring(text.length - 1, text.length);
            while (lastChar === '?' && text.length > 0) {
                text = text.substring(0, text.length - 1); // trim trailing '?' character
                lastChar = text.substring(text.length - 1, text.length);
            }
        }

        return text;
    } catch (error) {
        addLog(LOG_TYPE.ERROR, 'wordApiService.removeTrailingWildcards', error);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload:
                'Something went wrong with the Word add-in API, please try again. Contact your system administrator if this issue persists.'
        });
    }
}

// Select the full text
function selectLongTextWildcard(fullText, firstText, secondText, onSuccess) {
    let matchWildcards = true;
    try {
        let firstSearchText = removeLeadingWilstcards(firstText);
        firstSearchText = removeTrailingWildcards(firstSearchText);

        let secondSearchText = removeLeadingWilstcards(secondText);
        secondSearchText = removeTrailingWildcards(secondSearchText);

        Word.run(context => {
            const { body } = context.document;
            const searchOptions = {
                matchCase: false,
                ignoreSpace: true,
                ignorePunct: true,
                matchWildcards
            };

            const firstRange = body.search(firstSearchText, searchOptions);
            const secondRange = body.search(secondSearchText, searchOptions);

            context.load(firstRange);
            context.load(secondRange);
            return context
                .sync()
                .then(() => {
                    const range = firstRange.items[0].expandTo(secondRange.items[0]);
                    context.load(range, 'text, font');
                    searchCount++;
                    return context
                        .sync()
                        .then(() => {
                            onSuccess({ items: [range] });
                        })
                        .catch(() => store.dispatch({ type: DOCUMENT_SEARCH.SEARCH_FAILURE }));
                })
                .catch(() => store.dispatch({ type: DOCUMENT_SEARCH.SEARCH_FAILURE }));
        });
    } catch (error) {
        addLog(LOG_TYPE.ERROR, 'wordApiService.selectLongTextWildcard', error);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload:
                'Something went wrong with the Word add-in API, please try again. Contact your system administrator if this issue persists.'
        });
    }
}

export function searchInBodyWildcard(text, onSuccess) {
    let matchWildcards = true;
    try {
        let searchText = '';
        if (text.substring(0, 1) === '?') {
            searchText = text.substring(1); // trim leading '?' character
        } else {
            searchText = text;
        }

        if (searchText.substring(searchText.length - 1, searchText.length) === '?') {
            searchText = searchText.substring(0, searchText.length - 1); // trim trailing '?' character
        }

        // if the text contains any space characters then this cannot be a wildcard search
        if (searchText.indexOf(' ') > -1) {
            matchWildcards = false;
        }

        Word.run(context => {
            const { body } = context.document;
            const range = body.search(searchText, {
                matchCase: false,
                ignoreSpace: true,
                ignorePunct: true,
                matchWildcards
            });
            context.load(range, 'text, font');
            searchCount++;
            return context
                .sync()
                .then(() => onSuccess(range))
                .catch(() => store.dispatch({ type: DOCUMENT_SEARCH.SEARCH_FAILURE }));
        });
    } catch (error) {
        addLog(LOG_TYPE.ERROR, 'wordApiService.searchInBodyWildcard', error);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload:
                'Something went wrong with the Word add-in API, please try again. Contact your system administrator if this issue persists.'
        });
    }
}
/* Replace all the special characters with a '?' character */
function replaceSpecialCharacters(input) {
    try {
        let modifiedInput = '';
        const specialCharacters = ['[', ']', '(', ')', '{', '}', '*', '?', '<', '>', '!', '@'];
        [...input].forEach(c => {
            if (specialCharacters.includes(c)) {
                modifiedInput = `${modifiedInput}?`;
            } else {
                modifiedInput = `${modifiedInput}${c}`;
            }
        });

        return modifiedInput;
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'wordApiActions.replaceSpecialCharacters', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
        return input;
    }
}

// Something strange is going on here.  For some reason in React the wildcard search
// around special characters doesnt work properly causing MSWord to crash.  The same
// wildcard search works ok in pure javascript, but not here.
function escapeText(input) {
    try {
        const escapeChars = ['[', ']', '(', ')', '{', '}', '*', '?', '<', '>', '!', '@', '.'];
        let escapedText = '';
        let nextChar = '';

        for (let i = 0; i < input.length; i += 1) {
            nextChar = input[i];
            if (escapeChars.includes(nextChar)) {
                escapedText = `${escapedText}?`;
            } else {
                escapedText = `${escapedText}${nextChar}`;
            }
        }

        return escapedText;
    } catch (error) {
        addLog(LOG_TYPE.ERROR, 'wordApiService.escapeText', error);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload:
                'Something went wrong with the Word add-in API, please try again. Contact your system administrator if this issue persists.'
        });
    }
}

export function longSearchInBodyWildcard(onSuccess, originalText) {
    searchCount++;
    const matchWildcards = true;

    let text = originalText.split(' ').join('?');
    text = replaceSpecialCharacters(text);
    const inputLength = text.length;

    if (inputLength < 255) {
        if (originalText.substring(originalText.length - 1, originalText.length) === '?') {
            originalText = originalText.substring(0, originalText.length - 1); // trim trailing '?' character
        }

        navigateFirstRangeTruncateRightTextHandlerWildcard(
            escapeText(text),
            onSuccess,
            matchWildcards,
            escapeText(text),
            null,
            '',
            true,
            originalText
        );
        return;
    }

    // Search for the first 255 characters in the text
    const firstText = escapeText(text.substring(0, 255));

    // Search for the last 255 characters in the text
    let secondText = escapeText(text.substring(inputLength - 255, inputLength));
    if (secondText.substring(secondText.length - 1, secondText.length) === '?') {
        secondText = secondText.substring(0, secondText.length - 1); // trim trailing '?' character
    }
    navigateFirstRangeTruncateRightTextHandlerWildcard(
        text,
        onSuccess,
        matchWildcards,
        firstText,
        null,
        secondText,
        false,
        originalText
    );
}

// Locate the first 255 characters in the text.  If not found, reduce the search text by 25 characters (shortening from the right) and try again...and so on until we find something
function navigateFirstRangeTruncateRightTextHandlerWildcard(
    fullText,
    onSuccess,
    matchWildcards,
    input,
    searchResults,
    secondInput,
    firstRangeOnly,
    originalText
) {
    searchCount++;
    try {
        if (searchResults == null) {
            // start the first range search
            searchInBodyWildcard(input, res =>
                navigateFirstRangeTruncateRightTextHandlerWildcard(
                    fullText,
                    onSuccess,
                    matchWildcards,
                    input,
                    res,
                    secondInput,
                    firstRangeOnly,
                    originalText
                )
            );
            return;
        }

        if (searchResults.items.length > 0) {
            // found
            if (firstRangeOnly) {
                // select it and return
                searchInBodyWildcard(input, onSuccess);
            } else {
                // now locate the second range
                navigateSecondRangeTruncateLeftTextHandlerWildcard(
                    fullText,
                    onSuccess,
                    matchWildcards,
                    secondInput,
                    null,
                    input,
                    originalText
                );
            }
        } else if (input.length > 50) {
            // not found, but try again with a search string that's 25 characters shorter (shortening from the right)
            const reducedInput = input.substring(0, input.length - 25);
            searchInBodyWildcard(reducedInput, res =>
                navigateFirstRangeTruncateRightTextHandlerWildcard(
                    fullText,
                    onSuccess,
                    matchWildcards,
                    reducedInput,
                    res,
                    secondInput,
                    firstRangeOnly,
                    originalText
                )
            );
        } else {
            const startPos = fullText.indexOf('?') + 1;
            let endPos = fullText.length > 255 ? 255 : fullText.length;
            if (startPos > endPos) {
                store.dispatch({ type: DOCUMENT_SEARCH.SEARCH_FAILURE });
                return;
            }
            let firstText = fullText.substring(startPos, endPos); // truncate from the left
            endPos = firstText.lastIndexOf('?'); // locate last wildcard
            if (endPos === -1) {
                endPos = firstText.length;
            }
            firstText = firstText.substring(0, endPos); // trim last wildcard and text beyond it
            navigateFirstRangeTruncateLeftTextHandlerWildcard(
                fullText,
                onSuccess,
                matchWildcards,
                firstText,
                null,
                secondInput,
                firstRangeOnly,
                originalText
            );
        }
    } catch (error) {
        addLog(
            LOG_TYPE.ERROR,
            'wordApiService.navigateFirstRangeTruncateRightTextHandlerWildcard',
            error
        );
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload:
                'Something went wrong with the Word add-in API, please try again. Contact your system administrator if this issue persists.'
        });
    }
}

// Locate the first 255 characters in the text.  If not found, reduce the search by a single word at a time FROM THE LEFT and try again...and so on until we find something
function navigateFirstRangeTruncateLeftTextHandlerWildcard(
    fullText,
    onSuccess,
    matchWildcards,
    input,
    searchResults,
    secondInput,
    firstRangeOnly,
    originalText
) {
    searchCount++;
    try {
        if (searchResults == null) {
            // start the first range search
            searchInBodyWildcard(input, res =>
                navigateFirstRangeTruncateLeftTextHandlerWildcard(
                    fullText,
                    onSuccess,
                    matchWildcards,
                    input,
                    res,
                    secondInput,
                    firstRangeOnly,
                    originalText
                )
            );
            return;
        }

        if (searchResults.items.length > 0) {
            // found
            if (firstRangeOnly) {
                // select it and return
                searchInBodyWildcard(input, onSuccess);
            } else {
                // now locate the second range
                navigateSecondRangeTruncateLeftTextHandlerWildcard(
                    fullText,
                    onSuccess,
                    matchWildcards,
                    secondInput,
                    null,
                    input,
                    originalText
                );
            }
        } else if (input.length > 50) {
            // not found, but try again with a search string that's a one word shorter (shortening from the left) on each attempt
            const startPos = input.indexOf('?') + 1;
            const endPos = input.length > 255 ? 255 : input.length;
            if (startPos > endPos) {
                store.dispatch({ type: DOCUMENT_SEARCH.SEARCH_FAILURE });
                return;
            }
            const reducedInput = input.substring(startPos, endPos);
            searchInBodyWildcard(reducedInput, res =>
                navigateFirstRangeTruncateLeftTextHandlerWildcard(
                    fullText,
                    onSuccess,
                    matchWildcards,
                    reducedInput,
                    res,
                    secondInput,
                    firstRangeOnly,
                    originalText
                )
            );
        } else {
            // not found
            store.dispatch({ type: DOCUMENT_SEARCH.SEARCH_FAILURE });
        }
    } catch (error) {
        addLog(
            LOG_TYPE.ERROR,
            'wordApiService.navigateFirstRangeTruncateLeftTextHandlerWildcard',
            error
        );
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload:
                'Something went wrong with the Word add-in API, please try again. Contact your system administrator if this issue persists.'
        });
    }
}

// Locate the last 255 characters in the text.  If not found, reduce the search text by 30 characters and try again, and so on until we find something
function navigateSecondRangeTruncateLeftTextHandlerWildcard(
    fullText,
    onSuccess,
    matchWildcards,
    input,
    searchResults,
    firstText,
    originalText
) {
    searchCount++;
    try {
        if (searchResults == null) {
            // start the second range search
            searchInBodyWildcard(input, res =>
                navigateSecondRangeTruncateLeftTextHandlerWildcard(
                    fullText,
                    onSuccess,
                    matchWildcards,
                    input,
                    res,
                    firstText,
                    originalText
                )
            );
            return;
        }

        if (searchResults.items.length > 0) {
            // the firstText may have been truncated from the left slightly, so shorten then fullText by the same amount
            // so that the verification method continues to work
            const pos = fullText.indexOf(firstText);
            if (pos > -1) {
                fullText = fullText.substring(pos, fullText.length);
            }

            if (fullText.length === 0 || firstText.length === 0) {
                store.dispatch({ type: DOCUMENT_SEARCH.SEARCH_FAILURE });
                return;
            }

            // remove leading '?' character from both the fullText and firstText
            if (fullText.substring(0, 1) === '?') {
                fullText = fullText.substring(1, fullText.length);
            }
            if (firstText.substring(0, 1) === '?') {
                firstText = firstText.substring(1, firstText.length);
            }

            // found the second range
            selectLongTextWildcard(fullText, firstText, input, onSuccess);
        } else if (input.length > 30) {
            // not found, but try again with a search string that's 30 characters shorter (shortening from the left)
            const startPos = input.indexOf('?') + 1;
            const endPos = input.length > 255 ? 255 : input.length;
            if (startPos > endPos) {
                store.dispatch({ type: DOCUMENT_SEARCH.SEARCH_FAILURE });
                return;
            }

            const reducedInput = input.substring(startPos, endPos);
            searchInBodyWildcard(reducedInput, res =>
                navigateSecondRangeTruncateLeftTextHandlerWildcard(
                    fullText,
                    onSuccess,
                    matchWildcards,
                    reducedInput,
                    res,
                    firstText,
                    originalText
                )
            );
        } else {
            // not found, but try again with a search string that's shorter, one word at a time (shortening from the RIGHT)
            let reducedInput = fullText;
            if (reducedInput.length > 255) {
                reducedInput = reducedInput.substring(
                    reducedInput.length - 255,
                    reducedInput.length
                ); // get the last 255 characters of the full text
            }

            reducedInput = removeTrailingWildcards(reducedInput);
            reducedInput = removeLeadingWilstcards(reducedInput);

            const endPos = reducedInput.lastIndexOf('?');
            if (endPos === -1) {
                store.dispatch({ type: DOCUMENT_SEARCH.SEARCH_FAILURE });
                return;
            }

            reducedInput = reducedInput.substring(0, endPos); // truncate from the right, one word at a time

            searchInBodyWildcard(reducedInput, res =>
                navigateSecondRangeTruncateRightTextHandlerWildcard(
                    fullText,
                    onSuccess,
                    matchWildcards,
                    reducedInput,
                    res,
                    firstText,
                    originalText
                )
            );
        }
    } catch (error) {
        addLog(
            LOG_TYPE.ERROR,
            'wordApiService.navigateSecondRangeTruncateLeftTextHandlerWildcard',
            error
        );
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload:
                'Something went wrong with the Word add-in API, please try again. Contact your system administrator if this issue persists.'
        });
    }
}

// If not found, reduce the search text by one word at a time FROM THE RIGHT, and so on until we find something
function navigateSecondRangeTruncateRightTextHandlerWildcard(
    fullText,
    onSuccess,
    matchWildcards,
    input,
    searchResults,
    firstText,
    originalText
) {
    searchCount++;
    try {
        if (searchResults == null) {
            // start the second range search
            searchInBodyWildcard(input, res =>
                navigateSecondRangeTruncateRightTextHandlerWildcard(
                    fullText,
                    onSuccess,
                    matchWildcards,
                    input,
                    res,
                    firstText,
                    originalText
                )
            );
            return;
        }

        if (searchResults.items.length > 0) {
            // the firstText may have been truncated from the left slightly, so shorten then fullText by the same amount
            // so that the verification method continues to work
            const pos = fullText.indexOf(firstText);
            if (pos > -1) {
                fullText = fullText.substring(pos, fullText.length);
            }

            if (fullText.length === 0 || firstText.length === 0) {
                store.dispatch({ type: DOCUMENT_SEARCH.SEARCH_FAILURE });
                return;
            }

            // remove leading '?' character from both the fullText and firstText
            if (fullText.substring(0, 1) === '?') {
                fullText = fullText.substring(1, fullText.length);
            }
            if (firstText.substring(0, 1) === '?') {
                firstText = firstText.substring(1, firstText.length);
            }

            // found the second range
            selectLongTextWildcard(fullText, firstText, input, onSuccess);
        } else if (input.length > 30) {
            // not found, but try again with a search string that's 30 characters shorter (shortening from the RIGHT)
            let reducedInput = input;
            if (reducedInput.length > 255) {
                reducedInput = reducedInput.substring(0, 255);
            }

            reducedInput = removeTrailingWildcards(reducedInput);
            reducedInput = removeLeadingWilstcards(reducedInput);

            const endPos = reducedInput.lastIndexOf('?');
            if (endPos === -1) {
                store.dispatch({ type: DOCUMENT_SEARCH.SEARCH_FAILURE });
                return;
            }

            reducedInput = reducedInput.substring(0, endPos); // truncate from the right, one word at a time

            searchInBodyWildcard(reducedInput, res =>
                navigateSecondRangeTruncateRightTextHandlerWildcard(
                    fullText,
                    onSuccess,
                    matchWildcards,
                    reducedInput,
                    res,
                    firstText,
                    originalText
                )
            );
        } else {
            // not found
            store.dispatch({ type: DOCUMENT_SEARCH.SEARCH_FAILURE });
        }
    } catch (error) {
        addLog(
            LOG_TYPE.ERROR,
            'wordApiService.navigateSecondRangeTruncateRightTextHandlerWildcard',
            error
        );
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload:
                'Something went wrong with the Word add-in API, please try again. Contact your system administrator if this issue persists.'
        });
    }
}

/* WILDCARD METHODS END HERE  */

export function getCCCount() {
    return new Promise((resolve, reject) => {
        Word.run(context => {
            const ccList = context.document.contentControls;
            context.load(ccList);
            return context.sync().then(() => {
                resolve(ccList.items.length);
            });
        }).catch(err => {
            addLog(LOG_TYPE.ERROR, 'wordApiService.getCCCount', err);
            reject(err);
        });
    });
}

export function deleteDSChangeTagTextPromise() {
    return new Promise(resolve => {
        Word.run(context => {
            const { body } = context.document;
            const range = body.search('DSChangeTag', {
                matchCase: true,
                ignoreSpace: true,
                ignorePunct: true,
                matchWildcards: false,
                matchWholeWord: false
            });
            context.load(range, 'text, font');
            return context.sync().then(() => {
                range.items.forEach(item => {
                    item.delete();
                });
                context.sync();
                clearOoxmlStr(); // clear down
                resolve();
            });
        }).catch(err => {
            addLog(LOG_TYPE.ERROR, 'wordApiService.deleteDSChangeTagTextPromise', err);
            clearOoxmlStr(); // clear down
            resolve();
        });
    });
}

export function deleteDSChangeTagPromise() {
    return new Promise(resolve => {
        Word.run(context => {
            const contentControl = context.document.contentControls
                .getByTag('DSChangeTag')
                .getFirst();
            context.load(contentControl);
            return context.sync().then(() => {
                contentControl.delete(false);
                clearOoxmlStr(); // clear down
                resolve();
            });
        }).catch(err => {
            addLog(LOG_TYPE.ERROR, 'wordApiService.deleteDSChangeTagPromise', err);
            clearOoxmlStr(); // clear down
            resolve();
        });
    });
}

export function makeChangeToTrackPromise() {
    clearOoxmlStr(); // clear down
    return new Promise(resolve => {
        Word.run(context => {
            const { body } = context.document;
            const { paragraphs } = body;
            context.load(paragraphs);
            return context.sync().then(() => {
                // Add a hidden ContentControl at the end of the document to trigger the trackChange mechanism
                const range = paragraphs.items[paragraphs.items.length - 1].getRange('End');

                if (isWordonline()) {
                    const result = range.insertText('\nDSChangeTag');
                    context.load(result, 'font');
                    context.sync().then(() => {
                        result.font.color = 'White';
                        context.sync();
                    });
                } else {
                    const myContentControl = range.insertContentControl();
                    myContentControl.tag = 'DSChangeTag';
                    myContentControl.title = 'DSChangeTag';
                    myContentControl.insertText('\n', 'replace');
                    myContentControl.cannotDelete = false;
                    myContentControl.cannotEdit = false;
                    myContentControl.appearance = 'Hidden';
                }
                resolve();
            });
        });
    });
}

function trackChangesCountPromise() {
    return new Promise(resolve => {
        Word.run(context => {
            const ooxml = context.document.body.getOoxml();
            return context.sync().then(() => {
                const count = (ooxml.value.match(/w:ins/g) || []).length;
                let trackRevsions = false;
                if (ooxml.value.indexOf('w:trackRevisions') > -1) {
                    trackRevsions = true;
                }
                resolve({ count, trackRevsions }); // promise will resolve after sync resolves
            });
        }).catch(err => {
            addLog(LOG_TYPE.ERROR, 'wordApiService.trackChangesCountPromise', err);
            store.dispatch({
                type: DOCUMENT_MARKUP.CHECK_STALE_STATUS,
                payload: { staleStatus: '', staleResult: 'Stale' }
            });

            resolve({ count: -1, trackRevsions: false });
        });
    });
}

export function getOoxmlString() {
    ooxmlString = '';

    return new Promise(resolve => {
        Word.run(context => {
            const ooxml = context.document.body.getOoxml();
            return context.sync().then(() => {
                ooxmlString = ooxml.value;
                resolve(); // promise will resolve after sync resolves
            });
        }).catch(err => {
            addLog(LOG_TYPE.ERROR, 'wordApiService.getOoxmlString', err);
            store.dispatch({
                type: DOCUMENT_MARKUP.CHECK_STALE_STATUS,
                payload: { staleStatus: '', staleResult: 'Stale' }
            });

            resolve({ count: -1, trackRevsions: false });
        });
    });
}

export function ooxmlStr() {
    return ooxmlString;
}

export function clearOoxmlStr() {
    ooxmlString = '';
}

export function startTrackRevisionsEnabled() {
    let trackRevisionsEveryone = false;
    return new Promise(resolve => {
        trackChangesCountPromise().then(res => {
            trackRevisionsEveryone = res.trackRevsions;
            if (trackRevisionsEveryone === false) {
                makeChangeToTrackPromise();
            }
            resolve(res);
        });
    });
}

export function endTrackRevisionsEnabled(beforeCount) {
    let afterCount = -1;
    return new Promise(resolve => {
        trackChangesCountPromise().then(value => {
            afterCount = value.count;
            if (!isWordonline()) {
                deleteDSChangeTagPromise();
            } else {
                deleteDSChangeTagTextPromise();
            }
            if (afterCount > beforeCount) {
                resolve(true);
                return;
            }
            resolve(false);
        });
    });
}

function jumpToLabelsCCPlacement(input, searchResults, originalText, mapEntry, showMisses) {
    try {
        if (searchResults == null) {
            longSearchInBody(
                input,
                res => jumpToLabelsCCPlacement(input, res, originalText, mapEntry, showMisses),
                originalText
            );
            return;
        }

        if (searchResults.items.length > 0) {
            // found something
            if (showMisses) {
                // document is fully covered in black at this point,
                // now un-colour the hits to show the misses
                searchResults.items[0].font.color = missesFoundColour;
            } else {
                // colour the whole search hit as well, because
                // content controls cannot span multiple paragraphs
                searchResults.items[0].font.color = policyColour;

                const contentControl = searchResults.items[0].insertContentControl();
                contentControl.removeWhenEdited = false;
                contentControl.cannotDelete = false;
                contentControl.title = mapEntry.title;
                contentControl.tag = mapEntry.tag;
                contentControl.appearance = 'BoundingBox';
                contentControl.color = policyColour;
                contentControl.font.color = policyColour;
            }

            store.dispatch({ type: DOCUMENT_SEARCH.SEARCH_SUCCESS });
        } else if (input.length > 50) {
            // not found, try a small search string, shortening from the right, 25 characters at a time
            const reducedInput = input.substring(0, input.length - 25);
            longSearchInBody(
                reducedInput,
                res =>
                    jumpToLabelsCCPlacement(reducedInput, res, originalText, mapEntry, showMisses),
                originalText
            );
        } else {
            // not found
            store.dispatch({ type: DOCUMENT_SEARCH.SEARCH_FAILURE });
        }
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'wordApiService.jumpToLabelsCCPlacement', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

function jumpToHitsCCPlacement(input, searchResults, originalText, mapEntry) {
    try {
        if (searchResults == null) {
            longSearchInBody(
                input,
                res => jumpToHitsCCPlacement(input, res, originalText, mapEntry),
                originalText
            );
            return;
        }

        if (searchResults.items.length > 0) {
            // found something
            // colour the whole search hit as well, because
            // content controls cannot span multiple paragraphs
            switch (mapEntry.riskLevel) {
                case 'High':
                    searchResults.items[0].font.color = highRiskColour;
                    break;
                case 'Medium':
                    searchResults.items[0].font.color = mediumRiskColour;
                    break;
                case 'Low':
                    searchResults.items[0].font.color = lowRiskColour;
                    break;
                case 'N/A':
                    searchResults.items[0].font.color = naRiskColour;
                    break;
                default:
                    searchResults.items[0].font.color = mapEntry.color;
                    break;
            }

            store.dispatch({
                type: DOCUMENT_MARKUP.SEARCH_COUNT,
                payload: searchCount
            });

            // reset counter for next search
            searchCount = 0;

            const contentControl = searchResults.items[0].insertContentControl();
            contentControl.removeWhenEdited = false;
            contentControl.cannotDelete = false;
            contentControl.title = mapEntry.title;
            contentControl.tag = mapEntry.tag;
            contentControl.appearance = 'BoundingBox';
            contentControl.color = mapEntry.color;

            switch (mapEntry.riskLevel) {
                case 'High':
                    contentControl.font.color = highRiskColour;
                    break;
                case 'Medium':
                    contentControl.font.color = mediumRiskColour;
                    break;
                case 'Low':
                    contentControl.font.color = lowRiskColour;
                    break;
                case 'N/A':
                    contentControl.font.color = naRiskColour;
                    break;
                default:
                    contentControl.font.color = mapEntry.color;
                    break;
            }

            store.dispatch({ type: DOCUMENT_SEARCH.SEARCH_SUCCESS });
        } else if (input.length > 50) {
            // not found, try a small search string, shortening from the right, 25 characters at a time
            const reducedInput = input.substring(0, input.length - 25);
            longSearchInBody(
                reducedInput,
                res => jumpToHitsCCPlacement(reducedInput, res, originalText, mapEntry),
                originalText
            );
        } else {
            // not found
            store.dispatch({ type: DOCUMENT_SEARCH.SEARCH_FAILURE });
            store.dispatch({
                type: DOCUMENT_MARKUP.SEARCH_COUNT,
                payload: searchCount
            });

            // reset counter for next search
            searchCount = 0;
        }
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'wordApiService.jumpToHitsCCPlacement', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

export function deleteAllCCs() {
    return new Promise((resolve, reject) => {
        Word.run(context => {
            const ccList = context.document.contentControls;
            context.load(ccList);
            return context.sync().then(() => {
                // remove the CCs as well
                for (let i = 0; i < ccList.items.length; i += 1) {
                    ccList.items[i].delete(true);
                }

                return context
                    .sync()
                    .then(() => {
                        resolve();
                    })
                    .catch(err1 => {
                        addLog(LOG_TYPE.ERROR, 'wordApiService.deleteAllCCs', err1);
                        reject('Failed to delete content controls');
                    });
            });
        }).catch(err => {
            addLog(LOG_TYPE.ERROR, 'wordApiService.deleteAllCCs', err);
            reject('Failed to delete content controls');
        });
    });
}

export function ClearAllMarkup() {
    return new Promise((resolve, reject) => {
        Word.run(context => {
            const { body } = context.document;
            const { paragraphs } = body;
            context.load(paragraphs, 'font');
            return context.sync().then(() => {
                paragraphs.items.forEach(paragraph => {
                    paragraph.font.color = 'black';
                });

                deleteAllCCs()
                    .then(() => {
                        resolve();
                    })
                    .catch(err1 => {
                        addLog(LOG_TYPE.ERROR, 'wordApiService.ClearAllMarkup', err1);
                        reject('Failed to delete content controls');
                    });
            });
        }).catch(err => {
            addLog(LOG_TYPE.ERROR, 'wordApiService.ClearAllMarkup', err);
            reject('Failed to delete content controls');
        });
    });
}

export function contentControlClickEventHandler(bArgs) {
    const bindingName = bArgs.binding.id;

    Word.run(context => {
        const { body } = context.document;
        const ccList = body.contentControls;
        context.load(ccList);
        return context.sync().then(() => {
            for (let i = 0; i < ccList.items.length; i += 1) {
                const title = ccList.items[i].title;
                if (title.startsWith(bindingName)) {
                    const tag = ccList.items[i].tag;
                    if (title.substring(0, 1) === 'S') {
                        const topic = tag.split('|')[1];
                        const subtopic = tag.split('|')[2];
                        const startOffset = tag.split('|')[3];
                        const endOffset = tag.split('|')[4];

                        const selectedCC = {
                            type: 'Scorecard',
                            topic,
                            subtopic,
                            startOffset,
                            endOffset,
                            policyName: '',
                            tag,
                            bindingName
                        };

                        store.dispatch({
                            type: DOCUMENT_MARKUP.CONTENT_CONTROL_SELECTED,
                            payload: selectedCC
                        });
                    }

                    if (title.substring(0, 1) === 'L') {
                        const policyName = tag.split('|')[1];
                        const startOffset = tag.split('|')[2];
                        const endOffset = tag.split('|')[3];

                        const selectedCC = {
                            type: 'Label',
                            topic: '',
                            subtopic: '',
                            startOffset,
                            endOffset,
                            policyName,
                            tag,
                            bindingName
                        };

                        store.dispatch({
                            type: DOCUMENT_MARKUP.CONTENT_CONTROL_SELECTED,
                            payload: selectedCC
                        });
                    }
                }
            }
        });
    });
}

export function addContentControlBinding(ccTitle, id) {
    Office.context.document.bindings.addFromNamedItemAsync(
        ccTitle,
        Office.BindingType.Text,
        { id },
        function(result) {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                result.value.addHandlerAsync(
                    Office.EventType.BindingSelectionChanged,
                    contentControlClickEventHandler
                );
            } else {
                addLog(
                    LOG_TYPE.ERROR,
                    'wordApiService.addContentControlBinding',
                    result.error.message
                );
            }
        }
    );
}

export function addContentControlBindingOneByOne(theBindings, indx) {
    if (!theBindings) {
        store.dispatch({
            type: DOCUMENT_MARKUP.MARKUP_RUNNING,
            payload: 'ClickEventHandlersComplete'
        });

        return;
    }

    if (theBindings.length === 0) {
        store.dispatch({
            type: DOCUMENT_MARKUP.MARKUP_RUNNING,
            payload: 'ClickEventHandlersComplete'
        });

        return;
    }

    const ccTitle = theBindings[indx].title;
    const id = theBindings[indx].bindingName;
    Office.context.document.bindings.addFromNamedItemAsync(
        ccTitle,
        Office.BindingType.Text,
        { id },
        function(result) {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
                result.value.addHandlerAsync(
                    Office.EventType.BindingSelectionChanged,
                    contentControlClickEventHandler
                );
                indx++;
                if (indx < theBindings.length) {
                    const rootReducer = store.getState();
                    const { document } = rootReducer;
                    const { isMarkupCancelling } = document;
                    if (!isMarkupCancelling) {
                        setTimeout(() => addContentControlBindingOneByOne(theBindings, indx), 50);
                    }
                } else {
                    store.dispatch({
                        type: DOCUMENT_MARKUP.MARKUP_RUNNING,
                        payload: 'ClickEventHandlersComplete'
                    });

                    store.dispatch({ type: DOCUMENT_MARKUP.MARKUP_EVENTS_APPLIED, payload: true });
                }
            } else {
                addLog(
                    LOG_TYPE.ERROR,
                    'wordApiService.addContentControlBinding',
                    result.error.message
                );

                console.log(`Failed to add binding, error = ${result.error.message}`);
                addLog(
                    LOG_TYPE.ERROR,
                    'wordApiService.addContentControlBindingOneByOne',
                    result.error.message
                );

                // double check that we still have content controls in the document before
                // we retry with the event handler wire-up
                const isMarkupApplied = getFromStorage('MarkupApplied', STORAGE_TYPE.PROPERTY_BAG);
                if (isMarkupApplied && isMarkupApplied === 'Yes') {
                    if (
                        result.error.message
                            .toLowerCase()
                            .indexOf('wait until the previous call completes') === -1
                    ) {
                        // some other error, so just skip this next content control
                        indx++;
                    }

                    setTimeout(() => addContentControlBindingOneByOne(theBindings, indx), 1500);
                }
            }
        }
    );
}

export function addContentControlBindings() {
    const theBindings = [];
    Word.run(context => {
        const { body } = context.document;
        const ccList = body.contentControls;
        context.load(ccList);
        return context.sync().then(() => {
            for (let i = 0; i < ccList.items.length; i += 1) {
                const title = ccList.items[i].title;
                const bindingName = title.substring(0, 5);
                theBindings.push({ title, bindingName });
            }

            addContentControlBindingOneByOne(theBindings, 0);
        });
    });
}

export function removeContentControlBinding(id) {
    Office.select(`bindings#${id}`).removeHandlerAsync(
        Office.EventType.BindingSelectionChanged,
        function(result) {
            if (result.status !== Office.AsyncResultStatus.Succeeded) {
                addLog(
                    LOG_TYPE.ERROR,
                    'wordApiService.addContentControlBinding',
                    result.error.message
                );
            }
        }
    );
}

export function executeAllHits(mappedParagraphs) {
    // modify LIVING documnet
    Word.run(context => {
        const { body } = context.document;
        const { paragraphs } = body;
        context.load(paragraphs, 'font');
        return context.sync().then(() => {
            const { items } = paragraphs;

            mappedParagraphs.sort(sortByRiskSeverity).forEach(mapEntry => {
                const contentControl = items[mapEntry.paragraphIndx].insertContentControl();
                contentControl.title = mapEntry.title;
                contentControl.tag = mapEntry.tag;
                contentControl.removeWhenEdited = false;
                contentControl.cannotDelete = false;
                contentControl.appearance = 'BoundingBox';
                contentControl.color = mapEntry.color;
                switch (mapEntry.riskLevel) {
                    case 'High':
                        contentControl.font.color = highRiskColour;
                        break;
                    case 'Medium':
                        contentControl.font.color = mediumRiskColour;
                        break;
                    case 'Low':
                        contentControl.font.color = lowRiskColour;
                        break;
                    case 'N/A':
                        contentControl.font.color = naRiskColour;
                        break;
                    default:
                        contentControl.font.color = mapEntry.color;
                        break;
                }
            });
        });
    });
}

export function executeAllHits2(mappedParagraphs) {
    const mapEntry = mappedParagraphs[0];
    jumpToHitsCCPlacement(mapEntry.sealValue, null, mapEntry.sealValue, mapEntry);
}

export function dispatchHits(mappedParagraphs, mappedParagraphs2) {
    // modify LIVING documnet
    Word.run(context => {
        const { body } = context.document;
        const ccList = body.contentControls;
        context.load(ccList);
        return context.sync().then(() => {
            store.dispatch({
                type: DOCUMENT_MARKUP.BACKGROUND_PROCESS_SCORECARD,
                payload: {
                    mappedParagraphs,
                    mappedParagraphs2
                }
            });

            context.sync();
        });
    });
}

export function dispatchScorecardHits(mappedParagraphs, mappedParagraphs2, riskLevels) {
    // modify LIVING documnet
    Word.run(context => {
        const { body } = context.document;
        const ccList = body.contentControls;
        context.load(ccList);
        return context.sync().then(() => {
            const paragraphData = [];
            mappedParagraphs
                .filter(f => riskLevels.includes(f.riskLevel))
                .sort(sortByRiskSeverity)
                .forEach(mapEntry => {
                    paragraphData.push(mapEntry);
                });

            const paragraphData2 = [];
            mappedParagraphs2
                .filter(f => riskLevels.includes(f.riskLevel))
                .sort(sortByRiskSeverity)
                .forEach(mapEntry => {
                    paragraphData2.push(mapEntry);
                });

            store.dispatch({
                type: DOCUMENT_MARKUP.BACKGROUND_PROCESS_SCORECARD,
                payload: {
                    mappedParagraphs: paragraphData,
                    mappedParagraphs2: paragraphData2
                }
            });

            context.sync();
        });
    });
}

export function executeLabelsBatch(mappedParagraphs, showMisses) {
    // modify LIVING documnet
    Word.run(context => {
        const { body } = context.document;
        const { paragraphs } = body;
        context.load(paragraphs, 'font');
        return context.sync().then(() => {
            const { items } = paragraphs;

            if (showMisses) {
                // document is fully covered in black at this point,
                // now un-colour the hits to show the misses
                mappedParagraphs.forEach(mapEntry => {
                    paragraphs.items[mapEntry.paragraphIndx].font.color = missesFoundColour;
                });
            } else {
                // insert content controls for all the hits
                mappedParagraphs.sort(sortByStartOffset).forEach(mapEntry => {
                    items[mapEntry.paragraphIndx].font.color = policyColour;

                    const contentControl = items[mapEntry.paragraphIndx].insertContentControl();
                    contentControl.removeWhenEdited = false;
                    contentControl.cannotDelete = false;
                    contentControl.title = mapEntry.title;
                    contentControl.tag = mapEntry.tag;
                    contentControl.appearance = 'BoundingBox';
                    contentControl.color = policyColour;
                    contentControl.font.color = policyColour;
                });
            }

            return context.sync();
        });
    });
}

export function executeLabelsBatch2(mappedParagraphs, showMisses) {
    const mapEntry = mappedParagraphs[0];
    jumpToLabelsCCPlacement(mapEntry.sealValue, null, mapEntry.sealValue, mapEntry, showMisses);
}

export function dispatchLabelHits(mappedParagraphs, mappedParagraphs2, showMisses) {
    // Modify LIVING document
    Word.run(context => {
        const { body } = context.document;
        const { paragraphs } = body;
        context.load(paragraphs, 'font');
        const ccList = body.contentControls;
        context.load(ccList);
        return context.sync().then(() => {
            if (showMisses) {
                paragraphs.items.forEach(paragraph => {
                    paragraph.font.color = missesNotFoundColour;
                });
            }

            store.dispatch({
                type: DOCUMENT_MARKUP.BACKGROUND_PROCESS_LABELS,
                payload: { mappedParagraphs, mappedParagraphs2, showMisses }
            });

            return context.sync();
        });
    });
}

export function highlightAllHists(allHitsData, hitsToMarked, applyContentControls, showMisses) {
    Word.run(context => {
        const { body } = context.document;
        const { paragraphs } = body;
        context.load(paragraphs);
        return context.sync().then(() => {
            const documentParagraphs = [];
            paragraphs.items.forEach(paragraph => {
                documentParagraphs.push(paragraph.text);
            });

            const data = {
                documentParagraphs,
                allHitsData,
                hitsToMarked
            };

            store.dispatch({
                type: DOCUMENT_MARKUP.MARKUP_RUNNING,
                payload: 'Comparing'
            });

            markupWorker.postMessage(data);

            markupWorker.onmessage = e => {
                if (applyContentControls) {
                    store.dispatch({
                        type: DOCUMENT_MARKUP.MARKUP_RUNNING,
                        payload: 'Compared'
                    });
                } else {
                    const markupOp = getFromStorage('MarkupOperation', STORAGE_TYPE.PROPERTY_BAG);
                    if (markupOp === 'hits') {
                        store.dispatch({
                            type: DOCUMENT_MARKUP.MARKUP_RUNNING,
                            payload: 'ClickEventHandlersInProgress'
                        });
                    }
                }

                const { uniqueMappedParagraphs, uniqueMappedParagraphs2, allOverlaps } = e.data;

                store.dispatch({
                    type: DOCUMENT_MARKUP.OVERLAP_MAP,
                    payload: allOverlaps
                });

                if (applyContentControls) {
                    if (showMisses) {
                        dispatchLabelHits(
                            uniqueMappedParagraphs,
                            uniqueMappedParagraphs2,
                            showMisses
                        );
                    } else {
                        dispatchHits(uniqueMappedParagraphs, uniqueMappedParagraphs2);
                    }
                }
            };

            markupWorker.onerror = e => {
                store.dispatch({
                    type: DOCUMENT_MARKUP.MARKUP_FAILURE,
                    payload: true
                });
                addLog(LOG_TYPE.ERROR, 'wordApiService.highlightAllHists', e);
                store.dispatch({
                    type: NOTIFICATION_HANDLER.ERROR,
                    payload: 'Something went wrong with Markup The Document'
                });
            };
        });
    });
}

/* searchInBody - Search for a given text in document body.
    *Input*
        text: The given search text ( Max 255 characters ).
        onSuccess: The callback function on successful search.
            - Set to true if you are replacing spaces with ?
    *Output*
        Word.RangeCollection
*/
export function searchInBody(searchText, onSuccess) {
    try {
        Word.run(context => {
            const { body } = context.document;
            const range = body.search(searchText, {
                matchCase: false,
                ignoreSpace: true,
                ignorePunct: true,
                matchWildcards: false,
                matchWholeWord: false
            });
            context.load(range, 'text, font');
            searchCount++;
            return context
                .sync()
                .then(() => onSuccess(range))
                .catch(() => store.dispatch({ type: DOCUMENT_SEARCH.SEARCH_FAILURE }));
        });
    } catch (error) {
        addLog(LOG_TYPE.ERROR, 'wordApiService.searchInBody', error);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload:
                'Something went wrong with the Word add-in API, please try again. Contact your system administrator if this issue persists.'
        });
    }
}

/* longSearchInBody - Search for a given text ( More than 255 characters ) in document body.
    This is achieved by search for the first 255 characters and the last 255 characters in the text and creating a range between the two to select
    If either the first or second searches yeild no result, repeat the respective searches by 25 characters and search again, and so on, until
    a minimum of 50 characters, which would be deemed a failure to locate the text
    *Input*
        text: The given search text ( must be > 255 characters ).
        onSuccess: The callback function on successful search.
            - Set to true if you are replacing spaces with ?
    *Output*
        Word.Range
*/
export function longSearchInBody(text, onSuccess, originalText) {
    searchCount++;
    const inputLength = text.length;
    if (inputLength < 255) {
        navigateFirstRangeTruncateRightTextHandler(
            text,
            onSuccess,
            text,
            null,
            '',
            true,
            originalText
        );

        return;
    }

    // Search for the first 255 characters in the text
    // const firstText = escapeText(text.substring(0, 255));
    const firstText = text.substring(0, 255);

    // Search for the last 255 characters in the text
    // const secondText = escapeText(text.substring(inputLength - 255, inputLength));
    const secondText = text.substring(inputLength - 255, inputLength);

    navigateFirstRangeTruncateRightTextHandler(
        text,
        onSuccess,
        firstText,
        null,
        secondText,
        false,
        originalText
    );
}

// Locate the first 255 characters in the text.  If not found, reduce the search text by 25 characters (shortening from the right) and try again...and so on until we find something
function navigateFirstRangeTruncateRightTextHandler(
    fullText,
    onSuccess,
    input,
    searchResults,
    secondInput,
    firstRangeOnly,
    originalText
) {
    searchCount++;
    try {
        if (searchResults == null) {
            // start the first range search
            searchInBody(input, res =>
                navigateFirstRangeTruncateRightTextHandler(
                    fullText,
                    onSuccess,
                    input,
                    res,
                    secondInput,
                    firstRangeOnly,
                    originalText
                )
            );
            return;
        }

        if (searchResults.items.length > 0) {
            // found
            if (firstRangeOnly) {
                // select it and return
                searchInBody(input, onSuccess);
            } else {
                // now locate the second range
                navigateSecondRangeTruncateLeftTextHandler(
                    fullText,
                    onSuccess,
                    secondInput,
                    null,
                    input,
                    originalText
                );
            }
        } else if (input.length > 50) {
            // not found, but try again with a search string that's 25 characters shorter (shortening from the right)
            const reducedInput = input.substring(0, input.length - 25);
            searchInBody(reducedInput, res =>
                navigateFirstRangeTruncateRightTextHandler(
                    fullText,
                    onSuccess,
                    reducedInput,
                    res,
                    secondInput,
                    firstRangeOnly,
                    originalText
                )
            );
        } else {
            const startPos = fullText.indexOf(' ') + 1;
            let endPos = fullText.length > 255 ? 255 : fullText.length;
            if (startPos > endPos) {
                longSearchInBodyWildcard(onSuccess, originalText);
                return;
            }
            let firstText = fullText.substring(startPos, endPos); // truncate from the left
            endPos = firstText.lastIndexOf(' '); // locate last space character
            if (endPos === -1) {
                endPos = firstText.length;
            }
            firstText = firstText.substring(0, endPos); // trim last space character and text beyond it
            navigateFirstRangeTruncateLeftTextHandler(
                fullText,
                onSuccess,
                firstText,
                null,
                secondInput,
                firstRangeOnly,
                originalText
            );
        }
    } catch (error) {
        addLog(LOG_TYPE.ERROR, 'wordApiService.navigateFirstRangeTruncateRightTextHandler', error);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload:
                'Something went wrong with the Word add-in API, please try again. Contact your system administrator if this issue persists.'
        });
    }
}

// Locate the first 255 characters in the text.  If not found, reduce the search by a single word at a time FROM THE LEFT and try again...and so on until we find something
function navigateFirstRangeTruncateLeftTextHandler(
    fullText,
    onSuccess,
    input,
    searchResults,
    secondInput,
    firstRangeOnly,
    originalText
) {
    searchCount++;
    try {
        if (searchResults == null) {
            // start the first range search
            searchInBody(input, res =>
                navigateFirstRangeTruncateLeftTextHandler(
                    fullText,
                    onSuccess,
                    input,
                    res,
                    secondInput,
                    firstRangeOnly,
                    originalText
                )
            );
            return;
        }

        if (searchResults.items.length > 0) {
            // found
            if (firstRangeOnly) {
                // select it and return
                searchInBody(input, onSuccess);
            } else {
                // now locate the second range
                navigateSecondRangeTruncateLeftTextHandler(
                    fullText,
                    onSuccess,
                    secondInput,
                    null,
                    input,
                    originalText
                );
            }
        } else if (input.length > 50) {
            // not found, but try again with a search string that's a one word shorter (shortening from the left) on each attempt
            const startPos = input.indexOf(' ') + 1;
            const endPos = input.length > 255 ? 255 : input.length;
            if (startPos > endPos) {
                longSearchInBodyWildcard(onSuccess, originalText);
                return;
            }
            const reducedInput = input.substring(startPos, endPos);
            searchInBody(reducedInput, res =>
                navigateFirstRangeTruncateLeftTextHandler(
                    fullText,
                    onSuccess,
                    reducedInput,
                    res,
                    secondInput,
                    firstRangeOnly,
                    originalText
                )
            );
        } else {
            // not found
            longSearchInBodyWildcard(onSuccess, originalText);
        }
    } catch (error) {
        addLog(LOG_TYPE.ERROR, 'wordApiService.navigateFirstRangeTruncateLeftTextHandler', error);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload:
                'Something went wrong with the Word add-in API, please try again. Contact your system administrator if this issue persists.'
        });
    }
}

// Locate the last 255 characters in the text.  If not found, reduce the search text by 1 word (from the left), and so on until we find something
function navigateSecondRangeTruncateLeftTextHandler(
    fullText,
    onSuccess,
    input,
    searchResults,
    firstText,
    originalText
) {
    searchCount++;
    try {
        if (searchResults == null) {
            // start the second range search
            searchInBody(input, res =>
                navigateSecondRangeTruncateLeftTextHandler(
                    fullText,
                    onSuccess,
                    input,
                    res,
                    firstText,
                    originalText
                )
            );
            return;
        }

        if (searchResults.items.length > 0) {
            // the firstText may have been truncated from the left slightly, so shorten then fullText by the same amount
            // so that the verification method continues to work
            const pos = fullText.indexOf(firstText);
            if (pos > -1) {
                fullText = fullText.substring(pos, fullText.length);
            }

            if (fullText.length === 0 || firstText.length === 0) {
                longSearchInBodyWildcard(onSuccess, originalText);
                return;
            }

            // found the second range
            selectLongText(fullText, firstText, input, onSuccess);
        } else if (input.length > 30) {
            // not found, but try again with a smaller search string (shortening from the left one word at a time)
            const startPos = input.indexOf(' ') + 1;
            const endPos = input.length > 255 ? 255 : input.length;
            if (startPos > endPos) {
                longSearchInBodyWildcard(onSuccess, originalText);
                return;
            }

            const reducedInput = input.substring(startPos, endPos);
            searchInBody(reducedInput, res =>
                navigateSecondRangeTruncateLeftTextHandler(
                    fullText,
                    onSuccess,
                    reducedInput,
                    res,
                    firstText,
                    originalText
                )
            );
        } else {
            // not found, but try again with a search string that's shorter, one word at a time (shortening from the RIGHT)
            let reducedInput = fullText;
            if (reducedInput.length > 255) {
                reducedInput = reducedInput.substring(
                    reducedInput.length - 255,
                    reducedInput.length
                ); // get the last 255 characters of the full text
            }

            const endPos = reducedInput.lastIndexOf(' ');
            if (endPos === -1) {
                longSearchInBodyWildcard(onSuccess, originalText);
                return;
            }

            reducedInput = reducedInput.substring(0, endPos); // truncate from the right, one word at a time

            searchInBody(reducedInput, res =>
                navigateSecondRangeTruncateRightTextHandler(
                    fullText,
                    onSuccess,
                    reducedInput,
                    res,
                    firstText,
                    originalText
                )
            );
        }
    } catch (error) {
        addLog(LOG_TYPE.ERROR, 'wordApiService.navigateSecondRangeTruncateLeftTextHandler', error);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload:
                'Something went wrong with the Word add-in API, please try again. Contact your system administrator if this issue persists.'
        });
    }
}

// If not found, reduce the search text by one word at a time FROM THE RIGHT, and so on until we find something
function navigateSecondRangeTruncateRightTextHandler(
    fullText,
    onSuccess,
    input,
    searchResults,
    firstText,
    originalText
) {
    searchCount++;
    try {
        if (searchResults == null) {
            // start the second range search
            searchInBody(input, res =>
                navigateSecondRangeTruncateRightTextHandler(
                    fullText,
                    onSuccess,
                    input,
                    res,
                    firstText,
                    originalText
                )
            );
            return;
        }

        if (searchResults.items.length > 0) {
            // the firstText may have been truncated from the left slightly, so shorten then fullText by the same amount
            // so that the verification method continues to work
            const pos = fullText.indexOf(firstText);
            if (pos > -1) {
                fullText = fullText.substring(pos, fullText.length);
            }

            if (fullText.length === 0 || firstText.length === 0) {
                longSearchInBodyWildcard(onSuccess, originalText);
                return;
            }

            // found the second range
            selectLongText(fullText, firstText, input, onSuccess);
        } else if (input.length > 30) {
            // not found, but try again with a shorter search string that (shortening from the RIGHT one word at a time)
            let reducedInput = input;
            if (reducedInput.length > 255) {
                reducedInput = reducedInput.substring(0, 255);
            }

            const endPos = reducedInput.lastIndexOf(' ');
            if (endPos === -1) {
                longSearchInBodyWildcard(onSuccess, originalText);
                return;
            }

            reducedInput = reducedInput.substring(0, endPos); // truncate from the right, one word at a time

            searchInBody(reducedInput, res =>
                navigateSecondRangeTruncateRightTextHandler(
                    fullText,
                    onSuccess,
                    reducedInput,
                    res,
                    firstText,
                    originalText
                )
            );
        } else {
            // not found
            longSearchInBodyWildcard(onSuccess, originalText);
        }
    } catch (error) {
        addLog(LOG_TYPE.ERROR, 'wordApiService.navigateSecondRangeTruncateRightTextHandler', error);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload:
                'Something went wrong with the Word add-in API, please try again. Contact your system administrator if this issue persists.'
        });
    }
}

// Basic verification check to see if the select range is not an order of magnitude greater than the seal data
function verifySelection(fullText, rangeText) {
    const fullTextWordCount = fullText.split(' ').length;
    const rangeTextWordCount = rangeText.split(' ').length;
    const tolerance = fullTextWordCount * 0.5;
    if (
        rangeTextWordCount >= fullTextWordCount - tolerance &&
        rangeTextWordCount <= fullTextWordCount + tolerance
    ) {
        return true;
    }

    return false;
}

// Select the full text
function selectLongText(
    fullText,
    firstText,
    secondText,
    onSuccess,
    firstRangeIndx = 0,
    secondRangeIndx = 0
) {
    searchCount++;
    try {
        Word.run(context => {
            const { body } = context.document;
            const searchOptions = {
                matchCase: false,
                ignoreSpace: true,
                ignorePunct: true,
                matchWildcards: false,
                matchWholeWord: false
            };

            const firstRange = body.search(firstText, searchOptions);
            const secondRange = body.search(secondText, searchOptions);

            context.load(firstRange);
            context.load(secondRange);
            return context
                .sync()
                .then(() => {
                    const range = firstRange.items[firstRangeIndx].expandTo(
                        secondRange.items[secondRangeIndx]
                    );
                    context.load(range, 'text, font');
                    return context
                        .sync()
                        .then(() => {
                            if (verifySelection(fullText, range.text)) {
                                onSuccess({ items: [range] });
                            } else {
                                if (
                                    firstRange.items.length > 1 &&
                                    firstRangeIndx < firstRange.items.length
                                ) {
                                    firstRangeIndx += 1;
                                    selectLongText(
                                        fullText,
                                        firstText,
                                        secondText,
                                        onSuccess,
                                        firstRangeIndx,
                                        secondRangeIndx
                                    );
                                    return;
                                }

                                if (
                                    secondRange.items.length > 1 &&
                                    secondRangeIndx < secondRange.items.length
                                ) {
                                    secondRangeIndx += 1;
                                    selectLongText(
                                        fullText,
                                        firstText,
                                        secondText,
                                        onSuccess,
                                        firstRangeIndx,
                                        secondRangeIndx
                                    );
                                    return;
                                }

                                store.dispatch({ type: DOCUMENT_SEARCH.SEARCH_FAILURE });
                            }
                        })
                        .catch(() => store.dispatch({ type: DOCUMENT_SEARCH.SEARCH_FAILURE }));
                })
                .catch(() => store.dispatch({ type: DOCUMENT_SEARCH.SEARCH_FAILURE }));
        });
    } catch (error) {
        addLog(LOG_TYPE.ERROR, 'wordApiService.selectLongText', error);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload:
                'Something went wrong with the Word add-in API, please try again. Contact your system administrator if this issue persists.'
        });
    }
}

/* deleteAllContentControl - Deletes specific contentControl in document by tag.
    *Input*
        controlTag : The tag used when creating the contentControl
*/
export function deleteContentControlByTag(controlTag) {
    try {
        Word.run(context => {
            const contentControl = context.document.contentControls.getByTag(controlTag).getFirst();
            context.load(contentControl);
            return context.sync().then(() => {
                contentControl.delete(true);
            });
        });
    } catch (error) {
        addLog(LOG_TYPE.ERROR, 'wordApiService.deleteContentControlByTag', error);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload:
                'Something went wrong with the Word add-in API, please try again. Contact your system administrator if this issue persists.'
        });
    }
}

/* deleteAllContentControl - Deletes all content controls in document. */
export function deleteAllContentControl() {
    try {
        Word.run(context => {
            const { contentControls } = context.document.body;
            context.load(contentControls);
            return context.sync().then(() => {
                contentControls.items.forEach(contentControl => {
                    contentControl.delete(true);
                });
            });
        });
    } catch (error) {
        addLog(LOG_TYPE.ERROR, 'wordApiService.deleteAllContentControl', error);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload:
                'Something went wrong with the Word add-in API, please try again. Contact your system administrator if this issue persists.'
        });
    }
}

export function searchInParagraph(input, paragraphID, onSuccess) {
    try {
        Word.run(context => {
            const { paragraphs } = context.document.body;
            context.load(paragraphs, 'text');
            return context.sync().then(() => {
                const searchResults = paragraphs.items[paragraphID].search(input, {});
                context.load(searchResults, 'text, font');
                return context.sync().then(() => onSuccess(searchResults));
            });
        });
    } catch (error) {
        addLog(LOG_TYPE.ERROR, 'wordApiService.searchInParagraph', error);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload:
                'Something went wrong with the Word add-in API, please try again. Contact your system administrator if this issue persists.'
        });
    }
}

export function removeAllHighlight() {
    try {
        Word.run(context => {
            const { body } = context.document;
            context.load(body, 'text, font');
            const highLight = () => {
                body.font.highlightColor = '#FFFFFF';
            };
            return context.sync().then(highLight);
        });
    } catch (error) {
        addLog(LOG_TYPE.ERROR, 'wordApiService.removeAllHighlight', error);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload:
                'Something went wrong with the Word add-in API, please try again. Contact your system administrator if this issue persists.'
        });
    }
}

/* Insert clause text at cursor position
   This is a-little more complicate that you would think because of the way that MSWord handles carriage return characters (\n)
   A clause may include \n characters to separate paragraphs of text.  Whilst this inserts correctly in the main document body it does not insert
   properly in a bullet numbered list.  What occurs is that each \n increments the bullet numbering.  To work around this problem this method
   chops up (splits) the clause text on the \n character.  Then in reverse order inserts the text line-by-line with MSWord line-break characters inserted
   inbetween the lines
*/
export function insertTextAtCursor(text) {
    try {
        const textWithNormalizedLineEndings = text.replace(/\r/g, '\n');
        const lines = textWithNormalizedLineEndings.split('\n');
        Word.run(context => {
            const rng = context.document.getSelection();
            rng.clear();

            rng.select();

            for (let i = lines.length - 1; i >= 0; i -= 1) {
                const line = lines[i];
                context.document.getSelection().insertText(line, Word.InsertLocation.start);

                if (i !== 0) {
                    context.document.getSelection().insertBreak(Word.BreakType.line, 'Before');
                }
            }

            return context.sync();
        });
    } catch (error) {
        addLog(LOG_TYPE.ERROR, 'wordApiService.insertTextAtCursor', error);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload:
                'Something went wrong with the Word add-in API, please try again. Contact your system administrator if this issue persists.'
        });
    }
}
